import React, { useEffect, useRef, useCallback, useState, useImperativeHandle, forwardRef } from 'react';
import { Badge } from 'antd';
import { BellOutlined, MessageOutlined } from '@ant-design/icons';
import { getAlarmCount } from '@/service/jtAlarmConfig';
import { eventEmitter, EVENTS } from '@/utils/eventEmitter';

const POLL_INTERVAL = 20000; // 20秒轮询

export interface JtAlarmCountIconRef {
  refreshCount: () => void;
}

interface JtAlarmCountIconProps {
  style?: React.CSSProperties;
  onClick?: () => void;
}

const JtAlarmCountIcon = forwardRef<JtAlarmCountIconRef, JtAlarmCountIconProps>((props, ref) => {
  const { style, onClick } = props;
  const [count, setCount] = useState<number>(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef<boolean>(false);

  // 查询报警数量
  const fetchAlarmCount = useCallback(async () => {
    // 如果组件未挂载，不执行查询
    if (!isMountedRef.current) {
      return;
    }

    // 取消上一次未完成的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // 创建新的 AbortController
    abortControllerRef.current = new AbortController();

    try {
      const res = await getAlarmCount();
      // 确保组件仍然挂载
      if (isMountedRef.current && res?.data?.data !== undefined) {
        setCount(res.data.data ?? 0);
      }
    } catch (error: any) {
      // 忽略被中止的请求错误
      if (error?.name !== 'AbortError' && error?.message !== 'canceled') {
        console.error('获取报警数量失败:', error);
      }
    }
  }, []);

  // 暴露刷新方法给外部组件调用
  useImperativeHandle(ref, () => ({
    refreshCount: fetchAlarmCount,
  }));

  // 启动轮询
  const startPolling = useCallback(() => {
    // 清除之前的定时器
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // 立即执行一次查询
    fetchAlarmCount();

    // 设置轮询定时器
    timerRef.current = setInterval(() => {
      if (isMountedRef.current) {
        fetchAlarmCount();
      }
    }, POLL_INTERVAL);
  }, [fetchAlarmCount]);

  // 停止轮询
  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    // 取消进行中的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    startPolling();

    // 订阅刷新事件
    const unsubscribe = eventEmitter.on(EVENTS.REFRESH_ALARM_COUNT, () => {
      fetchAlarmCount();
    });

    return () => {
      isMountedRef.current = false;
      stopPolling();
      unsubscribe();
    };
  }, [startPolling, stopPolling, fetchAlarmCount]);

  // 格式化显示数量
  const displayCount = count > 99 ? '99+' : count;

  return (
    <span style={{ cursor: 'pointer', ...style }} onClick={onClick}>
      <Badge count={displayCount} size="small" offset={[-2, 2]}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',gap:4 }}>
          <MessageOutlined />
          <p style={{margin:0}}>报警推送</p>
        </div>
        {/* <BellOutlined style={{ fontSize: '18px', color: '#fff' }} /> */}
      </Badge>
    </span>
  );
});

JtAlarmCountIcon.displayName = 'JtAlarmCountIcon';

export default JtAlarmCountIcon;
